import type { UserDirectoryUpsert } from "../app/userDirectory/userDirectorySlice";

/**
 * メッセンジャー系エンティティ（プロジェクトメッセージ / ショットコメント / DM メッセージ）
 * に含まれる `author` / `receivers` / `files[].downloaded_users` から、
 * Redux userDirectory に upsert するための重複排除済みユーザー配列を抽出する。
 *
 * 各スコープのエンティティ型はフィールド名が完全に揃っているため、
 * ダックタイピング可能な最小構造で受け取る。
 */
interface UserLike {
  id?: number | string;
  display_name?: string | null;
  avatar?: string | null;
}

interface FileLike {
  downloaded_users?: UserLike[] | null;
}

interface MessengerEntityLike {
  author?: UserLike | null;
  receivers?: UserLike[];
  files?: FileLike[];
}

const addUserToMap = (
  map: Map<number, UserDirectoryUpsert>,
  user: UserLike | null | undefined,
): void => {
  if (!user || typeof user.id !== "number") return;
  map.set(user.id, {
    id: user.id,
    display_name: user.display_name ?? undefined,
    avatar: user.avatar ?? undefined,
  });
};

export const collectMessengerUsers = (
  entities: readonly MessengerEntityLike[],
): UserDirectoryUpsert[] => {
  const map = new Map<number, UserDirectoryUpsert>();
  for (const entity of entities) {
    addUserToMap(map, entity.author);
    for (const receiver of entity.receivers ?? []) {
      addUserToMap(map, receiver);
    }
    for (const file of entity.files ?? []) {
      for (const downloader of file.downloaded_users ?? []) {
        addUserToMap(map, downloader);
      }
    }
  }
  return Array.from(map.values());
};
